/**
 * Cross-browser fullscreen helpers.
 *
 * Standard Fullscreen API is not implemented uniformly:
 * - Safari (desktop & iPad) requires webkit-prefixed calls.
 * - Chromium/Firefox use the standard.
 * - iPhone Safari & iOS Chrome (WebKit) do NOT support element fullscreen
 *   for arbitrary DOM (only <video>). We still attempt webkit fallbacks
 *   so behaviour is best-effort. Callers should treat rejection as no-op.
 *
 * All functions swallow errors so UI never crashes on unsupported browsers.
 */

export function getFullscreenElement() {
  if (typeof document === 'undefined') return null;
  return (
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.webkitCurrentFullScreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement ||
    null
  );
}

export function requestFullscreen(el) {
  if (!el) return Promise.resolve(false);
  const fn =
    el.requestFullscreen ||
    el.webkitRequestFullscreen ||
    el.webkitRequestFullScreen ||
    el.mozRequestFullScreen ||
    el.msRequestFullscreen;
  if (!fn) return Promise.resolve(false);
  try {
    const r = fn.call(el);
    // webkit variants may return undefined instead of a Promise
    if (r && typeof r.then === 'function') {
      return r.then(() => true).catch(() => false);
    }
    return Promise.resolve(true);
  } catch {
    return Promise.resolve(false);
  }
}

export function exitFullscreen() {
  if (typeof document === 'undefined') return Promise.resolve(false);
  const fn =
    document.exitFullscreen ||
    document.webkitExitFullscreen ||
    document.webkitCancelFullScreen ||
    document.mozCancelFullScreen ||
    document.msExitFullscreen;
  if (!fn) return Promise.resolve(false);
  try {
    const r = fn.call(document);
    if (r && typeof r.then === 'function') {
      return r.then(() => true).catch(() => false);
    }
    return Promise.resolve(true);
  } catch {
    return Promise.resolve(false);
  }
}

export function toggleFullscreen(el) {
  return getFullscreenElement() ? exitFullscreen() : requestFullscreen(el);
}

export function onFullscreenChange(handler) {
  if (typeof document === 'undefined') return () => {};
  const events = [
    'fullscreenchange',
    'webkitfullscreenchange',
    'mozfullscreenchange',
    'MSFullscreenChange',
  ];
  events.forEach((e) => document.addEventListener(e, handler));
  return () => events.forEach((e) => document.removeEventListener(e, handler));
}
